import { Fragment, useContext, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useForm } from "react-hook-form";

import { Dialog, Transition } from "@headlessui/react";

import controlPanel from "common/action-function/controlPanel";
import { ModalPackageForm, ToastContext } from "common/contexts";
import getGAUserID from "common/fetch/getGAUserID";
import sendData from "common/fetch/post";
import trigger from "common/trigger";

import ButtonNew from "components/atoms/ButtonNew/index";
import { BiLoaderAlt } from "components/atoms/Icon/bi/BiLoaderAlt";
import { FaTimes } from "components/atoms/Icon/fa/FaTimes";
import Image from "components/atoms/Img";

import Step1 from "./step1";
import Step2 from "./step2";

/** ========== */

const PackageModal = (props) => {
  /* Variables Define */
  const { country_name, country_id, show, suffix_data, isReserved, onHide } =
    props;

  const {
    register,
    handleSubmit,
    reset,
    clearErrors,
    getValues,
    formState: { isSubmitting, errors },
  } = useForm();

  const { openAlert } = useContext(ToastContext);
  const [loading, setLoading] = useState(false);
  const recaptchaRef = useRef();
  const [selected, setSelected] = useState(suffix_data?.CompanySuffixes[0]);
  const [step, setStep] = useState(0);
  const companyName = getValues("company_name");

  /* Handle Event */

  const closeModal = () => {
    reset();
    clearErrors();
    setStep(0);
    setSelected(suffix_data.CompanySuffixes[0]);
    setLoading(false);
    onHide();
  };

  const onSubmit = async (data) => {
    const token = await recaptchaRef.current.executeAsync();

    if (token) {
      const GAUserID = getGAUserID();

      data.client_id = GAUserID ? GAUserID : "";
      data.company_suffix_id = parseInt(selected.id, 10);
      data.website_id = process.env.NEXT_PUBLIC_WEBSITE_ID;
      data.company_country_id = country_id;

      try {
        sendData({
          api: `${process.env.NEXT_PUBLIC_ORDER_API_ENDPOINT}/api/portal/contact_form/check_name`,
          data,
          token,
        })
          .then(async (res) => {
            if (res.state) {
              trigger("contact_form", data, `Check name - ${country_name}`);
              await openAlert(3000, "success", "", closeModal());
            } else {
              await openAlert(3000, "error");
              controlPanel("inline-flex");
              console.log("error");
            }
          })
          .catch((e) => {
            openAlert(3000, "error");
            controlPanel("inline-flex");
            console.log("error", e);
          });
      } catch (e) {
        clearErrors();
        openAlert(3000, "error");
        console.log("error", e);
        controlPanel("none");
      }
    }
  };

  return (
    <ModalPackageForm.Provider
      value={{
        register,
        isSubmitting,
        errors,
        step,
        setStep,
        getValues,
        country_id,
        suffix_data,
        isReserved,
        selected,
        setSelected,
      }}
    >
      <Transition appear show={show} as={"div"}>
        <Dialog
          as="div"
          unmount={true}
          className="fixed inset-0 overflow-y-auto"
          style={{ zIndex: 99 }}
          onClose={() => {}}
        >
          <div aria-hidden="true" className="min-h-screen px-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-slate-900 bg-opacity-70" />
            </Transition.Child>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span
              className="inline-block h-screen align-middle"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div
                className={
                  "popup-content-panel relative inline-flex  w-full  transform  flex-col justify-center rounded-2xl border-2 bg-gradient-to-br from-[#81F5FF] to-[#FFD5E5] px-4 py-10 text-center align-middle shadow-xl transition-all"
                }
                style={{ minHeight: "600px", maxWidth: "400px" }}
              >
                <div className={"absolute top-6 right-4 z-10"}>
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-full border-2 border-white p-1 text-xs  font-medium text-white transition-all  hover:border-bbc-red-300 hover:text-bbc-red-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    onClick={closeModal}
                  >
                    <FaTimes size={12} />
                  </button>
                </div>

                <div className=" relative z-10 ">
                  <form onSubmit={handleSubmit(onSubmit)}>
                    {step === 0 && (
                      <Fragment>
                        <h3 className="text-center text-3xl font-semibold leading-[44px] text-bbc-brand-700">
                          Check your company name availability in <br />{" "}
                          {country_name}
                        </h3>
                        <Step1 />
                      </Fragment>
                    )}
                    {step === 1 && (
                      <Fragment>
                        <h3 className="text-center text-3xl font-semibold leading-[44px] text-bbc-brand-700">
                          How we can reach you out about result of {companyName}{" "}
                          {selected.name}
                        </h3>
                        <Step2 />
                        <ReCAPTCHA
                          ref={recaptchaRef}
                          sitekey={
                            process.env.NEXT_PUBLIC_G_RECAPTCHA_V3_SITE_KEY
                          }
                          badge="inline"
                          size="invisible"
                          className="my-6 flex justify-center "
                        />

                        <ButtonNew
                          type="submit"
                          othersClass="min-w-[170px] relative"
                          disabled={loading}
                          onClick={() => {
                            if (isSubmitting) setLoading(true);
                          }}
                        >
                          {isSubmitting && (
                            <BiLoaderAlt
                              className="absolute left-4 top-2.5 animate-spin"
                              size={20}
                            />
                          )}
                          Check Now
                        </ButtonNew>
                        <a
                          className="mt-3 block cursor-pointer text-base text-white"
                          onClick={() => {
                            setStep(step - 1);
                          }}
                        >
                          {" "}
                          <u>Go back</u>
                        </a>
                      </Fragment>
                    )}
                  </form>
                </div>
                <div className="absolute bottom-0 left-0 h-full w-full overflow-hidden rounded-2xl">
                  <Image
                    src="/assets/images/common/package_popup_bg.png"
                    width={400}
                    height={427}
                    alt="Background popup"
                    wrapper={{
                      className: "absolute w-full h-auto left-0 z-0 ",
                      style: { bottom: "-80px" },
                    }}
                  />
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </ModalPackageForm.Provider>
  );
};

export default PackageModal;

PackageModal.propTypes = {};

PackageModal.defaultProps = {};

/** ========== */
